'use client';
import clsx from 'clsx';
import { useState, useCallback } from 'react';
import { useSegmentInteraction } from '../../../../lib/analytics/useSegmentInteraction';
import {
  type L2Menu,
  type PromoSpot as PromoSpotType,
} from '../../../../services/globalNavItems/types';
import { FullscreenOverlay } from '../../../FullscreenOverlay';
import { ArrowRight } from '../../../Icons/ArrowRight';
import { SegmentAwareAnchor } from '../../../SegmentLink';
import { Typography } from '../../../Typography';
import { L3Navigation } from '../L3Navigation';
import { CloseButton } from '../../../CloseButton';
import { PromoSpot } from './PromoSpot';

export type L2NavigationProps = {
  l2Menu: L2Menu;
  l1Title: string;
  open: boolean;
  onClose: () => void;
  moreInfoLink?: {
    linkText: string;
    uri: string;
  };
  promoSpot?: PromoSpotType;
  id: string;
};

export function L2Navigation({
  l2Menu,
  l1Title,
  open,
  onClose,
  moreInfoLink,
  promoSpot,
  id,
}: L2NavigationProps) {
  const [selectedL2NavItem, setSelectedL2NavItem] = useState<number | null>(null);

  const sendSegmentInteraction = useSegmentInteraction({
    eventName: 'global_nav_menu_interacted',
    attributes: {
      entity_name: 'global_nav_l2',
      item_level: 2,
      navigation_format: 'header',
      l1_nav_copy: l1Title,
    },
  });

  const handleMouseEnter = useCallback((index: number | null, title?: string) => {
    setSelectedL2NavItem(index);
    if (title && index !== null) {
      sendSegmentInteraction({ item_copy: title, item_position: index });
    }
  }, []);

  return (
    <div
      id={id}
      className={clsx(
        'absolute',
        'top-[82px]',
        'left-0',
        'right-0',
        'max-h-[calc(100svh-82px)]',
        'bg-white',
        {
          block: open,
          hidden: !open,
        },
      )}
    >
      <FullscreenOverlay isOpen={open} onClick={onClose} />
      <div className={clsx('w-full', 'h-full')}>
        <div className={clsx('flex', 'gap-6', 'ml-page-gutter')}>
          <div className={clsx('mb-4', 'flex')}>
            <ul className={clsx('flex', 'flex-col', 'p-0', 'm-0')}>
              {l2Menu.categoryLinks.map((link, index) => {
                return (
                  <li className={clsx('flex', 'list-none')} key={link.title}>
                    {link.itemLink ? (
                      <SegmentAwareAnchor
                        segmentAttributes={{
                          destination_url: link.itemLink.uri,
                          entity_name: 'global_nav_l2_link',
                          item_copy: link.title,
                          item_level: 2,
                          item_position: index,
                          l1_nav_copy: l1Title,
                          navigation_format: 'header',
                        }}
                        onMouseEnter={() => handleMouseEnter(null)}
                        href={`${link.itemLink.uri}`}
                        className={clsx(
                          'text-xsmall',
                          'font-medium',
                          'no-underline',
                          'text-inherit',
                          'py-2',
                          'px-4',
                          'bg-white',
                          'rounded-[.25rem]',
                          'w-fit',
                          'tap-highlight-transparent',
                          'hover:text-green-default',
                          'hover:underline',
                        )}
                        aria-label={`Navigate to ${link.title}`}
                      >
                        {link.title}
                      </SegmentAwareAnchor>
                    ) : (
                      <button
                        onMouseEnter={() => handleMouseEnter(index, link.title)}
                        aria-expanded={selectedL2NavItem === index}
                        aria-controls={`desktop-nav-level-2-menu-${link.title}`}
                        className={clsx(
                          'whitespace-nowrap',
                          'cursor-pointer',
                          'py-2',
                          'px-4',
                          'border-0',
                          'text-left',
                          'rounded-[.25rem]',
                          'w-fit',
                          'bg-transparent',
                          'flex',
                          'flex-row',
                          'gap-2',
                          'items-center',
                          'hover:bg-green-lightest',
                        )}
                      >
                        <Typography variant="text-xsmall" className={clsx('font-medium')}>
                          {link.title}
                        </Typography>
                        {link.l3Links && <ArrowRight />}
                      </button>
                    )}
                  </li>
                );
              })}
              {moreInfoLink && (
                <li className={clsx('flex', 'list-none')}>
                  <SegmentAwareAnchor
                    segmentAttributes={{
                      destination_url: moreInfoLink.uri,
                      entity_name: 'global_nav_l2_link',
                      item_copy: moreInfoLink.linkText,
                      item_level: 2,
                      item_position: l2Menu.categoryLinks.length,
                      l1_nav_copy: l1Title,
                      navigation_format: 'header',
                    }}
                    href={`${moreInfoLink.uri}`}
                    onMouseEnter={() => setSelectedL2NavItem(null)}
                    className={clsx(
                      'text-xsmall',
                      'font-medium',
                      'no-underline',
                      'text-inherit',
                      'py-2',
                      'px-4',
                      'bg-white',
                      'rounded-[.25rem]',
                      'w-fit',
                      'tap-highlight-transparent',
                      'hover:text-green-default',
                      'hover:underline',
                    )}
                    aria-label={`Navigate to ${moreInfoLink.linkText}`}
                  >
                    {moreInfoLink.linkText}
                  </SegmentAwareAnchor>
                </li>
              )}
            </ul>
          </div>
          {l2Menu.categoryLinks.map((link, index) => (
            <L3Navigation
              isOpen={selectedL2NavItem !== null ? selectedL2NavItem === index : false}
              aria-hidden={selectedL2NavItem !== index}
              id={`desktop-nav-level-2-menu-${link.title}`}
              key={link.title}
              l3Links={link.l3Links}
              l1Title={l1Title}
              l2Title={link.title}
              toolsResourcesSection={link.toolsResourcesSection}
              moreInfoLink={
                link?.moreInfoText && link.moreInfoLink?.uri
                  ? {
                      linkText: link.moreInfoText,
                      uri: link.moreInfoLink.uri,
                    }
                  : undefined
              }
            />
          ))}
          {promoSpot && <PromoSpot promoSpot={promoSpot} open={open} />}
        </div>
      </div>
      <CloseButton onClick={onClose} />
    </div>
  );
}
